/**
 * JURO 3D viewer — lazy load, overlay gate
 * ------------------------------------------------------------------
 * The real model is what the section shows, not a photograph. three.js and
 * the racket (~2.5 MB) load when the section approaches the viewport, so the
 * homepage's initial payload is unaffected but the model is already turning
 * by the time anyone scrolls to it.
 *
 * A button overlays the canvas until it is pressed. That is deliberate:
 *  - it signals the thing is interactive rather than a still image
 *  - it absorbs pointer events, so scrolling past the section scrolls the
 *    page instead of being swallowed by the 3D controls, which is otherwise
 *    the single most irritating thing about an embedded 3D viewer on a phone
 *
 * Progressive enhancement: the markup is inert without JS and the section
 * simply shows nothing rather than breaking.
 */

(() => {
  const SELECTOR = '[data-viewer]';

  /** Load the viewer script once, reusing the promise for later sections. */
  const loaders = new Map();
  function loadScript(src) {
    if (loaders.has(src)) return loaders.get(src);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => reject(new Error('viewer script failed to load')), { once: true });
      document.head.appendChild(script);
    });
    loaders.set(src, promise);
    return promise;
  }

  /** Bring in three.js and mount the custom element. */
  async function mount(wrap) {
    if (wrap.dataset.viewerState) return;
    const src = wrap.dataset.viewerSrc;
    if (!src) return;

    wrap.dataset.viewerState = 'loading';

    try {
      await loadScript(src);
      if (window.customElements) await customElements.whenDefined('juro-racket-3d');

      const viewer = document.createElement('juro-racket-3d');
      // Insert before the overlay so the overlay stays on top.
      wrap.prepend(viewer);
      wrap.dataset.viewerState = 'ready';
    } catch (error) {
      console.error('[juro-viewer]', error);
      wrap.dataset.viewerState = 'error';
      const button = wrap.querySelector('[data-viewer-load]');
      if (button) button.textContent = 'Could not load 3D';
    }
  }

  /** Dismiss the overlay so the model becomes directly interactive. */
  function activate(wrap) {
    const overlay = wrap.querySelector('[data-viewer-overlay]');
    wrap.dataset.viewerActive = 'true';
    if (overlay) {
      overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
      // If the transition is suppressed (reduced motion), remove directly.
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) overlay.remove();
    }
    const viewer = wrap.querySelector('juro-racket-3d');
    if (viewer) {
      viewer.setAttribute('tabindex', '-1');
      viewer.focus({ preventScroll: true });
    }
  }

  function init() {
    for (const wrap of document.querySelectorAll(SELECTOR)) {
      if (wrap.dataset.wired) continue;
      wrap.dataset.wired = 'true';

      const button = wrap.querySelector('[data-viewer-load]');
      if (button) {
        button.addEventListener('click', async () => {
          // If someone reaches the button before the observer has fired,
          // load on demand rather than making them wait for a scroll.
          await mount(wrap);
          activate(wrap);
        });
      }

      if (!('IntersectionObserver' in window)) {
        mount(wrap);
        continue;
      }

      /*
       * On a phone, or when the visitor has asked for reduced data, wait for
       * the button instead of pre-fetching.
       *
       * three.js plus the model is about 1.3 MB, and this section sits second
       * on the homepage — so with a 400px margin it was fetching almost
       * immediately, before anyone had scrolled or shown any interest in the
       * 3D. On desktop that buys a model already turning when you arrive; on
       * a phone on cellular it is a large download for a feature the visitor
       * has not asked for. The button still loads it on demand.
       */
      const saveData = navigator.connection?.saveData === true;
      const smallScreen = window.matchMedia('(max-width: 749px)').matches;
      if (saveData || smallScreen) continue;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.disconnect();
            mount(wrap);
          }
        },
        // Start fetching before it is on screen so the model is turning by
        // the time the section is reached.
        { rootMargin: '400px 0px' }
      );
      observer.observe(wrap);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
